import React, { useContext, useState } from 'react';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { OrderInput } from '../../../../../../types/order';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import CreateOrderProductPane from './CreateOrderProductPane';
import ToggleButton from '../../../../Common/ToggleButton';
import CreateOutboundOrderProductContainerPane from './CreateOutboundOrderProductContainerPane';
import { Container } from '../../../../../../types/container';
import { Company } from '../../../../../../types/company';
import { UserContext } from '../../../../../../context/UserContext';
import { FlowType } from '../../../../../../types/order/orderFlow';

enum ProductInfoActionView {
  product = 'product',
  container = 'container',
}

function productInfoActionViewToLocalizedString(view: string) {
  switch (view) {
    case ProductInfoActionView.product:
      return t(TranslationKey.product).singular.label;
    case ProductInfoActionView.container:
      return t(TranslationKey.container).singular.label;
  }

  return '';
}

interface CreateOrderProductInfoPaneProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  next?: () => void;
  back?: () => void;
  add?: () => void;
  cancel?: () => void;
  loading?: boolean;
  setError: (err: string) => void;
  modalHeight: string;
  validContainers: Container[];
  disabled?: boolean;
}

export default function CreateOrderProductInfoPane({
  order,
  setOrder,
  next,
  back,
  add,
  cancel,
  setError,
  loading = false,
  modalHeight,
  validContainers,
  disabled = false,
}: CreateOrderProductInfoPaneProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { currentUser } = useContext(UserContext);

  const [view, setView] = useState<ProductInfoActionView>(ProductInfoActionView.product);

  const hasContainers =
    currentCompany.settings.featureToggles.containers.containers &&
    currentCompany.settings.featureToggles.orders.productBasedApproach;

  const content = () => {
    if (view === ProductInfoActionView.product) {
      const scanRequired = Company.hasScanRequired(currentCompany, currentUser || undefined);

      if (scanRequired) {
        return (
          <div className='mx-6 mt-2 border border-warning-300 bg-warning-100 px-2 py-2 text-center text-warning-400'>
            <p className='text-sm'>{t(TranslationKey.scanRequiredOrderWarning).singular.label}</p>
          </div>
        );
      }

      return (
        <CreateOrderProductPane
          order={order}
          setOrder={setOrder}
          setError={setError}
          next={next}
          back={back}
          loading={loading}
          disabled={disabled}
        />
      );
    } else if (view === ProductInfoActionView.container) {
      if (order.type === FlowType.inbound) return null;
      // TODO: Split this up for inbound and outbound orders when we get to it
      return (
        <CreateOutboundOrderProductContainerPane
          order={order}
          setOrder={setOrder}
          next={next}
          back={back}
          setError={setError}
          loading={loading}
          validContainers={validContainers}
        />
      );
    }
  };

  return (
    <div className={'flex h-full flex-col pb-3'}>
      <div className='mx-6 mt-2'>
        {hasContainers && order.type !== FlowType.inbound ? (
          <ToggleButton
            onChange={value => setView(value as ProductInfoActionView)}
            selected={view}
            values={Object.keys(ProductInfoActionView)}
            text={value => productInfoActionViewToLocalizedString(value)}
          />
        ) : null}
      </div>
      <div className='flex-grow'>{content()}</div>
    </div>
  );
}
